import { useCallback, useMemo, useState } from "react";

import { Holding } from "../../../../../services/holdings";
import { DEFAULT_HOLDING_SORT, HOLDINGS_PAGE_SIZE } from "./constants";
import {
  clampPage,
  filterHoldings,
  hasStatus,
  holdingCounts,
  pageCountOf,
  pageSlice,
  sortHoldings,
} from "./holdingsWorkspace";
import {
  HoldingAssetFilter,
  HoldingSortKey,
  HoldingSortState,
  HoldingStatusFilter,
  HoldingsWorkspace,
} from "./types";

const EMPTY_ROWS: Holding[] = [];

export const useHoldingsWorkspace = (
  holdings: Holding[] | undefined,
): HoldingsWorkspace => {
  const [query, setQueryState] = useState("");
  const [status, setStatusState] = useState<HoldingStatusFilter>("open");
  const [assetGroup, setAssetGroupState] = useState<HoldingAssetFilter>("ALL");
  const [sort, setSort] = useState<HoldingSortState>(DEFAULT_HOLDING_SORT);
  const [page, setPage] = useState(0);

  const all = holdings ?? EMPTY_ROWS;

  // Account-wide, not the status partition: the pending-price banner must keep counting price work
  // that the Closed facet or a search would otherwise hide.
  const openRows = useMemo(() => all.filter((row) => !row.isClosed), [all]);

  const statusRows = useMemo(
    () => all.filter((row) => hasStatus(row, status)),
    [all, status],
  );

  const filteredRows = useMemo(
    () => sortHoldings(filterHoldings(statusRows, { assetGroup, query }), sort),
    [statusRows, assetGroup, query, sort],
  );

  const counts = useMemo(
    () => holdingCounts(all, openRows, statusRows, filteredRows),
    [all, openRows, statusRows, filteredRows],
  );

  const pageCount = pageCountOf(filteredRows.length, HOLDINGS_PAGE_SIZE);
  // Clamped on read rather than synced by an effect: a filter that shrinks the list
  // would otherwise render one empty frame before the effect pulled the page back.
  const safePage = clampPage(page, pageCount);
  const visibleRows = useMemo(
    () => pageSlice(filteredRows, safePage, HOLDINGS_PAGE_SIZE),
    [filteredRows, safePage],
  );

  const setQuery = useCallback((next: string) => {
    setQueryState(next);
    setPage(0);
  }, []);

  const setStatus = useCallback((next: HoldingStatusFilter) => {
    setStatusState(next);
    setPage(0);
  }, []);

  const setAssetGroup = useCallback((next: HoldingAssetFilter) => {
    setAssetGroupState(next);
    setPage(0);
  }, []);

  // A second click on the active column flips it; a new column starts descending,
  // which is the useful end of every column here except the instrument name.
  const toggleSort = useCallback((key: HoldingSortKey) => {
    setSort((current) =>
      current.key === key
        ? { key, dir: current.dir === "asc" ? "desc" : "asc" }
        : { key, dir: key === "instrument" ? "asc" : "desc" },
    );
    setPage(0);
  }, []);

  const clearFilters = useCallback(() => {
    setQueryState("");
    setStatusState("open");
    setAssetGroupState("ALL");
    setPage(0);
  }, []);

  return useMemo(
    () => ({
      query,
      status,
      assetGroup,
      sort,
      page: safePage,
      openRows,
      statusRows,
      filteredRows,
      visibleRows,
      counts,
      pageCount,
      setQuery,
      setStatus,
      setAssetGroup,
      setSort,
      toggleSort,
      setPage,
      clearFilters,
    }),
    [
      query,
      status,
      assetGroup,
      sort,
      safePage,
      openRows,
      statusRows,
      filteredRows,
      visibleRows,
      counts,
      pageCount,
      setQuery,
      setStatus,
      setAssetGroup,
      toggleSort,
      clearFilters,
    ],
  );
};
